import { useMutation } from '@apollo/client';
import { XMarkIcon } from '@heroicons/react/24/solid';
import type { JSONContent } from '@tiptap/react';
import { endOfDay, startOfDay, startOfMonth, subMonths } from 'date-fns';
import { UserProfile } from 'interfaces';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Editor, { generate } from 'components/editor';

import {
  addNewComplaint,
  getAllComplaints,
  getClientComplaints,
} from 'queries/complaints';

import { useBase } from 'providers/base';
import { useOrganization } from 'providers/organization';
import { useUI } from 'providers/ui';

import { ComplaintClientSearch, ComplaintMemberSearch } from './search-fields';

const isEditorContentEmpty = (value?: string | null) =>
  !value ||
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length === 0;

const AddComplaint = ({
  closeModal,
  dateState,
  client,
  callData,
}: {
  closeModal: () => void;
  dateState: any;
  client?: UserProfile;
  callData?: any;
}) => {
  const { clients } = useBase();
  const { team } = useOrganization();
  const ui = useUI();

  const [note, setNote] = useState(false);
  const [selected, setSelected] = useState<UserProfile | null>(null);
  const [assignTo, setAssignedTo] = useState<any | null>(null);

  const [insertNewComplaint] = useMutation(addNewComplaint);

  const {
    register: registercomplaints,
    handleSubmit: handleComplaints,
    setValue: setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      message: '',
      id: '',
      resolution: '',
      client: '',
      member: '',
      note: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (client) {
      setValue('client', client.id);
      setSelected(client);
    }
  }, [client, setValue]);

  const title = watch('title');
  const message = watch('message');
  const clientId = watch('client');
  const memberId = watch('member');

  const canSubmit = useMemo(
    () =>
      !!title?.trim() &&
      !isEditorContentEmpty(message) &&
      !!clientId &&
      !!memberId,
    [clientId, memberId, message, title],
  );

  const handleComplaintBodyUpdate = (json: JSONContent) => {
    const html = generate(json);
    setValue('message', isEditorContentEmpty(html) ? '' : html, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // insert new complaint
  const insertComplaint = async (data) => {
    try {
      const payload: { [key: string]: any } = {
        user_id: data.client,
        title: data.title,
        complaint: data.message,
        assignee_id: data.member || null,
      };

      if (data.note) {
        payload.notes = {
          data: [
            {
              note: {
                data: {
                  content: data.note,
                },
              },
            },
          ],
        };
      }

      if (callData) {
        payload.calls = {
          data: [
            {
              call_id: callData.id,
            },
          ],
        };
      }

      const result = await insertNewComplaint({
        variables: {
          data: payload,
        },
        refetchQueries: [
          {
            query: getAllComplaints,
            variables: {
              start: dateState?.controlOption?.value
                ? startOfDay(dateState.controlOption.value.start)
                : startOfMonth(startOfDay(subMonths(new Date(), 3))),
              end: dateState?.controlOption?.value
                ? endOfDay(dateState.controlOption.value.end)
                : endOfDay(new Date()),
            },
          },
          {
            query: getClientComplaints,
            variables: {
              id: data.client,
            },
          },
        ],
      });

      if (result) {
        ui.setToast(true, {
          type: 'success',
          title: 'Complaint Added Successfully',
          message: '',
        });
      }
      reset({
        title: '',
        message: '',
        client: '',
        member: '',
        note: '',
      });
      setSelected(null);
      setAssignedTo(null);
      closeModal();
    } catch (error) {
      console.log(error, 'error');
      ui.setToast(true, {
        type: 'error',
        title: 'Error',
        message: 'Something went wrong',
      });
      console.log(error);
      reset({
        title: '',
        message: '',
        client: '',
        member: '',
        note: '',
      });
      setSelected(null);
      setAssignedTo(null);
      closeModal();
    }
  };

  return (
    <section className="flex flex-1 h-full justify-between flex-col divide-y divide-gray-200 bg-white overflow-hidden">
      <div className="flex overflow-scroll flex-col">
        <form
          className="space-y-4"
          onSubmit={handleComplaints(insertComplaint)}
        >
          <div className="bg-white space-y-5 p-4">
            <div className="flex flex-col-2 space-y-2">
              <div className="flex-wrap w-full flex-col space-y-4">
                <div className="py-2">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-semibold text-sm text-slate-900">
                      Complaint title
                    </span>
                    <span className="text-xs text-slate-400">
                      Short internal headline
                    </span>
                  </div>
                  <input
                    type="text"
                    name="title"
                    {...registercomplaints('title', { required: true })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    placeholder="Summarise the complaint in one line"
                  />
                </div>

                <div className="border-t border-slate-100 py-2">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-semibold text-sm text-slate-900">
                      Complaint message
                    </span>
                    <span className="text-xs text-slate-400">
                      Rich text supported
                    </span>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <Editor
                      content={message || null}
                      shortMenu
                      onUpdate={handleComplaintBodyUpdate}
                      placeholder="Describe what happened, when it happened, and what resolution is being requested."
                      className="min-h-[200px] max-w-none prose-sm"
                      classes="overflow-hidden rounded-2xl"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Capture the full complaint clearly so the assignee can act
                    without reopening the case for context.
                  </p>
                </div>

                <input
                  type="hidden"
                  {...registercomplaints('message', { required: true })}
                />
                <input
                  type="hidden"
                  {...registercomplaints('client', { required: true })}
                />
                <input
                  type="hidden"
                  {...registercomplaints('member', { required: true })}
                />

                <ComplaintClientSearch
                  clients={clients || []}
                  selected={selected}
                  onChange={(value) => {
                    setSelected(value);
                    setValue('client', value.id, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onClear={() => {
                    setSelected(null);
                    setValue('client', '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />

                <ComplaintMemberSearch
                  members={team || []}
                  selected={assignTo}
                  onChange={(value) => {
                    setAssignedTo(value);
                    setValue('member', value.id, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onClear={() => {
                    setAssignedTo(null);
                    setValue('member', '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />

                {/*Add Note*/}
                <div className="relative z-0 w-full">
                  {note === true ? (
                    <div className="relative mt-3 flex flex-1 flex-col items-start border-t border-slate-100 pt-4">
                      <button
                        type="button"
                        className="absolute right-0 top-4 rounded-full border border-gray-300 p-[2px] text-gray-400 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-purple-500 focus-visible:border-purple-500"
                        onClick={() => {
                          setNote(false);
                        }}
                      >
                        <XMarkIcon className="w-3" />
                      </button>
                      <p className="font-semibold text-sm text-gray-700">
                        Note
                      </p>
                      <textarea
                        name="note"
                        className="mt-3 w-full rounded-xl border border-slate-200 bg-white p-3 pr-8 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                        placeholder="Add note"
                        rows={4}
                        {...registercomplaints('note', {
                          required: false,
                        })}
                        spellCheck={true}
                      />
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-1 space-x-2 border-t border-slate-100 pt-4">
                      <button
                        type="button"
                        className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-100"
                        onClick={() => setNote(true)}
                      >
                        Add a note
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full place-content-end pr-4">
            <button
              type="submit"
              disabled={!canSubmit}
              className="panel-primary-button"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
export default AddComplaint;
